import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Severity = 'critical' | 'warning' | 'success';

type DimensionRow = {
  name: string;
  score: number;
  delta: number;
  severity: Severity;
  note: string;
};

const SAMPLE_ORG = {
  name: 'Acme Payments API',
  domain: 'api.acme-payments.example',
  ranAt: 'May 1, 2026',
  spec: 'openapi.yaml @ 7f3c2a1',
  before: 54,
  after: 88,
};

const DIMENSIONS: DimensionRow[] = [
  {
    name: 'llms.txt presence & quality',
    score: 0,
    delta: 100,
    severity: 'critical',
    note: 'No llms.txt served. Athena drafted one from your OpenAPI spec.',
  },
  {
    name: 'OpenAPI completeness',
    score: 64,
    delta: 28,
    severity: 'warning',
    note: '12 operations missing examples; 4 responses lack schemas.',
  },
  {
    name: 'Auth & error semantics',
    score: 78,
    delta: 14,
    severity: 'warning',
    note: 'Error envelope inconsistent on 4 endpoints; no rate-limit headers documented.',
  },
  {
    name: 'SDK consistency',
    score: 41,
    delta: 41,
    severity: 'critical',
    note: 'TypeScript SDK lags spec by 2 versions; Python SDK drops 3 optional fields.',
  },
  {
    name: 'Time-to-first-call',
    score: 52,
    delta: 32,
    severity: 'warning',
    note: 'Agent took 9 turns and 4m 12s. Hit a 401 before finding the auth header doc.',
  },
  {
    name: 'Code samples & cookbook',
    score: 70,
    delta: 18,
    severity: 'warning',
    note: 'cURL only; no TypeScript or Python. Webhooks recipe missing signature verification.',
  },
  {
    name: 'MCP server presence',
    score: 0,
    delta: 95,
    severity: 'critical',
    note: 'No MCP server. Athena will host one generated from your OpenAPI.',
  },
  {
    name: 'Cursor / Copilot rules',
    score: 0,
    delta: 90,
    severity: 'critical',
    note: 'No .cursorrules. Athena will publish rules reflecting current SDK shape.',
  },
  {
    name: 'Changelog discipline',
    score: 82,
    delta: 6,
    severity: 'success',
    note: 'Versioned changelog present. Add machine-readable feed for full credit.',
  },
  {
    name: 'Stability & observability',
    score: 88,
    delta: 4,
    severity: 'success',
    note: 'Idempotency keys and request IDs present. Status page is up.',
  },
];

const TOP_FIXES = [
  {
    title: 'Publish llms.txt and host the MCP server',
    impact: '+22 points',
    detail:
      'Athena auto-drafted both from your OpenAPI document. Adding them recovers two full dimensions and is the single highest-leverage change.',
  },
  {
    title: 'Reconcile TypeScript SDK with the latest spec',
    impact: '+12 points',
    detail:
      "The current `@acme/payments@2.4.x` is missing two endpoints and three optional fields agents rely on for refunds. Regenerate from spec and bump to 2.6.",
  },
  {
    title: 'Add missing examples to 12 OpenAPI operations',
    impact: '+9 points',
    detail:
      'Operations under /payouts and /disputes have no request/response examples. Agents fall back to guessing payload shapes.',
  },
  {
    title: 'Standardize error envelope across all endpoints',
    impact: '+6 points',
    detail:
      "Four endpoints return `{ message }` while the rest return `{ error: { code, message } }`. Agents can't write a single error handler.",
  },
  {
    title: 'Add TypeScript and Python code samples',
    impact: '+5 points',
    detail:
      'Cookbook ships cURL only. Adding TS and Python samples for the top 6 recipes (auth, list, create, webhook verify, retries, pagination) closes the gap.',
  },
];

export function SampleReportPage() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-20">
      <section>
        <div className="mb-5 inline-block rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
          Sample report
        </div>
        <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">
          What you get back from an audit.
        </h1>
        <p className="mt-4 max-w-3xl text-lg text-slate-600 dark:text-slate-400">
          This is the actual report shape Athena produces — recreated here on a fictional payments
          API. Every audit ships with the same sections: headline score, dimension breakdown, top
          fixes, and a projected post-fix score.
        </p>
      </section>

      <section className="mt-12 rounded-xl border border-slate-200 bg-white p-8 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Audited surface
            </div>
            <div className="mt-1 text-2xl font-semibold tracking-tight">{SAMPLE_ORG.name}</div>
            <div className="mt-1 font-mono text-sm text-slate-500">{SAMPLE_ORG.domain}</div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-mono text-xs text-slate-500">
              <span>Ran {SAMPLE_ORG.ranAt}</span>
              <span>{SAMPLE_ORG.spec}</span>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <ScoreDial label="Today" score={SAMPLE_ORG.before} tone="warning" />
            <ArrowRight />
            <ScoreDial label="After top 5 fixes" score={SAMPLE_ORG.after} tone="success" />
          </div>
        </div>
      </section>

      <section className="mt-16">
        <h2 className="text-2xl font-semibold tracking-tight">Dimension breakdown</h2>
        <p className="mt-2 max-w-2xl text-slate-600 dark:text-slate-400">
          Each dimension has its own 0–100 score. Delta shows how many points are recoverable on
          this dimension if the recommended fix ships.
        </p>

        <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-950">
              <tr>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-400">
                  Dimension
                </th>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-400">Score</th>
                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-400">
                  Recoverable
                </th>
                <th className="hidden px-4 py-3 font-medium text-slate-600 dark:text-slate-400 md:table-cell">
                  Finding
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
              {DIMENSIONS.map((d) => (
                <tr key={d.name}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <SeverityDot severity={d.severity} />
                      <span className="font-medium">{d.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <ScoreBar score={d.score} severity={d.severity} />
                  </td>
                  <td className="px-4 py-4 font-mono text-xs text-[var(--color-accent)]">
                    +{d.delta}
                  </td>
                  <td className="hidden px-4 py-4 text-slate-600 dark:text-slate-400 md:table-cell">
                    {d.note}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-16">
        <h2 className="text-2xl font-semibold tracking-tight">Top 5 fixes, by score impact</h2>
        <p className="mt-2 max-w-2xl text-slate-600 dark:text-slate-400">
          Athena ranks recommendations by score impact × ease-to-fix. Shipping these five takes{' '}
          {SAMPLE_ORG.name} from {SAMPLE_ORG.before} to {SAMPLE_ORG.after}.
        </p>
        <ol className="mt-8 space-y-4">
          {TOP_FIXES.map((f, i) => (
            <li
              key={f.title}
              className="flex gap-4 rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="font-mono text-2xl font-semibold text-slate-300 tabular-nums dark:text-slate-700">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-semibold">{f.title}</h3>
                  <span className="rounded-md bg-[var(--color-accent)]/10 px-2 py-0.5 font-mono text-xs font-semibold text-[var(--color-accent)]">
                    {f.impact}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{f.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-16 rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-xl font-semibold tracking-tight">Generated assets included</h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
          The report ships with the assets we drafted from your spec. Copy them in or have Athena
          host them for you on the Team plan and above.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <AssetTile name="llms.txt" path="/.well-known/llms.txt" />
          <AssetTile name="MCP server" path="mcp.acme-payments.example" />
          <AssetTile name=".cursorrules" path="/.cursorrules" />
        </div>
      </section>

      <section className="mt-20 rounded-xl border border-slate-200 bg-slate-50 p-8 text-center dark:border-slate-800 dark:bg-slate-950">
        <h2 className="text-2xl font-semibold tracking-tight">Get this report for your API.</h2>
        <p className="mx-auto mt-2 max-w-xl text-slate-600 dark:text-slate-400">
          The free audit produces this exact report on your own surface. No credit card needed.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Button size="lg" asChild>
            <Link to="/signup">Run a free audit</Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/methodology">Read the methodology</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function ScoreDial({
  label,
  score,
  tone,
}: {
  label: string;
  score: number;
  tone: 'warning' | 'success';
}) {
  const color = tone === 'success' ? 'var(--color-success)' : 'var(--color-warning)';
  return (
    <div className="text-center">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div
        className="mt-2 flex h-24 w-24 items-center justify-center rounded-full"
        style={{
          background: `conic-gradient(${color} ${score * 3.6}deg, rgb(226 232 240) 0deg)`,
        }}
      >
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white dark:bg-slate-900">
          <span className="font-mono text-2xl font-semibold tabular-nums" style={{ color }}>
            {score}
          </span>
        </div>
      </div>
    </div>
  );
}

function ArrowRight() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-6 w-6 text-slate-400"
    >
      <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SeverityDot({ severity }: { severity: Severity }) {
  const tone =
    severity === 'critical'
      ? 'bg-[var(--color-critical)]'
      : severity === 'warning'
        ? 'bg-[var(--color-warning)]'
        : 'bg-[var(--color-success)]';
  return <span className={cn('inline-block h-2 w-2 flex-shrink-0 rounded-full', tone)} />;
}

function ScoreBar({ score, severity }: { score: number; severity: Severity }) {
  const tone =
    severity === 'critical'
      ? 'var(--color-critical)'
      : severity === 'warning'
        ? 'var(--color-warning)'
        : 'var(--color-success)';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div
          className="h-full rounded-full"
          style={{ width: `${score}%`, background: tone }}
        />
      </div>
      <span className="font-mono text-xs font-semibold tabular-nums">{score}</span>
    </div>
  );
}

function AssetTile({ name, path }: { name: string; path: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
      <div className="text-sm font-semibold">{name}</div>
      <div className="mt-1 truncate font-mono text-xs text-slate-500" title={path}>
        {path}
      </div>
    </div>
  );
}
